import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { FolderIcon, Skeleton, Tag, Text } from '@mercai/clever'

import { CardInfo } from '@/components'

import { SaleOrderStatusEnum } from '@/types'
import { CardSupplyOrderOrdersDetailProps } from './card-supply-order-orders-detail.type'

import { formatLongDate } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { useSaleOrderVariantList } from '@/services'
import { Link } from 'react-router-dom'
import styles from './card-supply-order-orders-detail.module.css'

const CardSupplyOrderOrdersDetailLoading = () => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  return (
    <CardInfo
      icon={<FolderIcon />}
      title={t('orderPaymentShowSummaryValuestitle')}
    >
      <div>
        <div className={styles['card-item']}>
          <div>
            <Text>
              <Skeleton width="8rem" height="1rem" />
            </Text>
          </div>
          <div>
            <Text>
              <Skeleton width="7rem" height="1rem" />
            </Text>
          </div>
          <div>
            <Text>
              <Skeleton width="9rem" height="1rem" />
            </Text>
          </div>
          <div>
            <Text>
              <Skeleton width="9.5rem" height="1rem" />
            </Text>
          </div>
        </div>
      </div>
    </CardInfo>
  )
}

export const CardSupplyOrderOrdersDetail = ({
  saleOrders = [],
  isLoading,
}: CardSupplyOrderOrdersDetailProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplyOrderDetail')

  const { data, isLoading: isLoadingOrders } = useSaleOrderVariantList({
    variantFields: 'simplifiedList',
    reference: saleOrders.join(','),
    page: 1,
    limit: 99,
  })

  const isLoadingAux = isLoading || isLoadingOrders

  const statuses: Record<SaleOrderStatusEnum, ReactNode> = {
    created: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('sale_order_status_created')}
      </Tag>
    ),
    processing: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('sale_order_status_processing')}
      </Tag>
    ),
    reviewed: (
      <Tag variant="primary" model="light" size="small">
        {tEnums('sale_order_status_reviewed')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light" size="small">
        {tEnums('sale_order_status_completed')}
      </Tag>
    ),
    fraudulent: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('sale_order_status_fraudulent')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('sale_order_status_cancelled')}
      </Tag>
    ),
  }

  if (isLoadingAux) {
    return <CardSupplyOrderOrdersDetailLoading />
  }

  return (
    <CardInfo
      icon={<FolderIcon />}
      title={t('cardSupplyOrderOrdersDetailTitle')}
    >
      {saleOrders.length === 0 ? (
        <Text>{t('cardSupplyOrderOrdersDetailNoOrdersLabel')}</Text>
      ) : (
        <div>
          {data?.data?.results?.map((order) => (
            <div key={order?.reference} className={styles['card-item']}>
              <div>
                <Text>
                  <strong>
                    {t('cardSupplyOrderOrdersDetailReferenceLabel')}
                    {': '}
                  </strong>
                  <Link
                    to={ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.fullPath(
                      order?.uuid || '',
                    )}
                    target="_blank"
                    className={styles.link}
                  >
                    {order?.reference}
                  </Link>
                </Text>
              </div>

              <div className={styles.status}>
                <Text>
                  <strong>
                    {t('cardSupplyOrderOrdersDetailStatusLabel')}
                    {': '}
                  </strong>
                </Text>
                {statuses?.[order?.status]}
              </div>

              <div>
                <Text>
                  <strong>
                    {t('cardSupplyOrderOrdersDetailCreatedAtLabel')}
                    {': '}
                  </strong>
                  {formatLongDate(order?.createdAt)}
                </Text>
              </div>

              <div>
                <Text>
                  <strong>
                    {t('cardSupplyOrderOrdersDetailKamLabel')}
                    {': '}
                  </strong>
                  {order?.keyAccountManager
                    ? `${order?.keyAccountManager?.name} - ${order?.keyAccountManager?.email}`
                    : t('notInformedLabel')}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardInfo>
  )
}
